// The operator integration-health view (P13): what the dashboards show, in the portal, for the
// person on call. Numbers first, then the failed events with a retry, then the alarms.

import { ApiErrorSchema, type Me } from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { ApiError } from "../api";

interface HealthView {
  at: string;
  gauges: Record<string, number | null>;
  outbox: {
    pending: number;
    failed: number;
    inFlight: number;
    oldestPendingAgeMs: number | null;
    publishedLastHour: number;
    pendingByType: Record<string, number>;
    triageReplayAfterMs?: number;
    waitingTriageJobs?: Array<{
      id: string;
      aggregateId: string;
      waitingAgeMs: number;
    }>;
    failedEvents: Array<{
      id: string;
      eventType: string;
      aggregateType: string;
      aggregateId: string;
      attempts: number;
      lastError: string | null;
      failedAt: string | null;
    }>;
  };
  inbox: { received24h: number; unfinished: number };
  mail: { sent24h: number; bounced24h: number; complained24h: number; failed24h: number };
  attachments: { scanning: number; oldestScanningAgeMs: number | null; infected24h: number };
  board: { hold: boolean; holdReason: string | null };
  alarms: Array<{
    id: number;
    action: string;
    entityType: string;
    entityId: string;
    after: unknown;
    createdAt: string;
  }>;
}

function age(ms: number | null): string {
  if (ms === null) return "none";
  const minutes = Math.round(ms / 60_000);
  return minutes < 60 ? `${minutes} min` : `${Math.round(minutes / 60)} h`;
}

async function load(): Promise<HealthView> {
  const res = await fetch("/api/admin/health/integration", { credentials: "same-origin" });
  if (!res.ok) throw new ApiError({ status: res.status, code: "unknown", message: res.statusText });
  return (await res.json()) as HealthView;
}

export function AdminHealth({ me }: { me: Me }) {
  const [view, setView] = useState<HealthView | null>(null);
  const [notice, setNotice] = useState("");
  const [retrying, setRetrying] = useState(false);
  const isAdmin = me.user.roles.includes("admin");

  useEffect(() => {
    load()
      .then(setView)
      .catch(() => setNotice("The health view could not be loaded."));
  }, []);

  async function retry(eventId: string, mode: "same" | "successor") {
    const reason = window.prompt(`Why retry ${eventId} (${mode})?`);
    if (!reason || reason.trim().length < 3) return;
    setRetrying(true);
    try {
      const res = await fetch(`/api/admin/health/outbox/${eventId}/retry`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json", "x-csrf-token": me.csrfToken },
        body: JSON.stringify({ mode, reason }),
      });
      if (!res.ok) {
        const error = ApiErrorSchema.safeParse(await res.json().catch(() => undefined));
        setNotice(error.success ? error.data.error.message : `Retry refused (${res.status}).`);
        return;
      }
      setNotice(`Retry recorded for ${eventId}.`);
      try {
        setView(await load());
      } catch {
        setNotice("Retry recorded. Refresh the page to check its progress.");
      }
    } catch {
      setNotice("Could not confirm the retry. Refresh the page before trying again.");
    } finally {
      setRetrying(false);
    }
  }

  if (!view) return <p role="status">{notice || "Loading…"}</p>;
  const g = view.gauges;
  const row = (label: string, value: string | number | null) => (
    <tr key={label}>
      <th scope="row" style={{ textAlign: "left", paddingRight: "1rem" }}>
        {label}
      </th>
      <td>{value === null ? "—" : value}</td>
    </tr>
  );

  return (
    <section aria-labelledby="health-heading">
      <h2 id="health-heading">Integration health</h2>
      <p>
        As of {new Date(view.at).toLocaleString()}.{" "}
        {view.board.hold ? (
          <strong style={{ color: "#a4000f" }}>
            Board hold is active: {view.board.holdReason}
          </strong>
        ) : (
          "Board writes are enabled."
        )}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(18rem, 1fr))",
          gap: "1rem",
        }}
      >
        <table>
          <caption style={{ textAlign: "left", fontWeight: "bold" }}>Intake and board</caption>
          <tbody>
            {row("Requests not on the board", g["requests.unsynced"] ?? 0)}
            {row("Oldest unsynced", age(g["requests.unsynced_oldest_age_ms"] ?? null))}
            {row("No triage outcome yet", g["requests.without_triage_outcome"] ?? 0)}
            {row("Cards created (24 h)", g["board.cards_created_24h"] ?? 0)}
            {row(
              "Self-service / staff-raised (24 h)",
              `${g["intake.self_service_24h"] ?? 0} / ${g["intake.staff_raised_24h"] ?? 0}`,
            )}
            {row("Detector rejections (24 h)", g["intake.rejections_24h"] ?? 0)}
          </tbody>
        </table>
        <table>
          <caption style={{ textAlign: "left", fontWeight: "bold" }}>Triage</caption>
          <tbody>
            {row("Waiting for a person", g["triage.queue_depth"] ?? 0)}
            {row("Oldest waiting", age(g["triage.queue_oldest_age_ms"] ?? null))}
            {row("Runs (24 h)", g["triage.runs_24h"] ?? 0)}
            {row(
              "Gate pass rate (24 h)",
              g["triage.gate_pass_rate_24h"] === null ||
                g["triage.gate_pass_rate_24h"] === undefined
                ? null
                : `${Math.round((g["triage.gate_pass_rate_24h"] ?? 0) * 100)}%`,
            )}
            {row(
              "Written / reused / queued / stopped",
              `${g["triage.written_24h"] ?? 0} / ${g["triage.reused_24h"] ?? 0} / ${g["triage.queued_24h"] ?? 0} / ${g["triage.stopped_24h"] ?? 0}`,
            )}
            {row("Ambiguous duplicates", g["triage.ambiguous_24h"] ?? 0)}
            {row("Draft-off runs", g["triage.draft_off_runs_24h"] ?? 0)}
            {row("Coverage gaps", g["triage.coverage_gaps_24h"] ?? 0)}
            {row("Revision conflicts", g["triage.revision_conflicts_24h"] ?? 0)}
          </tbody>
        </table>
        <table>
          <caption style={{ textAlign: "left", fontWeight: "bold" }}>
            Outbox, inbox, mail, attachments
          </caption>
          <tbody>
            {row(
              "Outbox pending / in flight / failed",
              `${view.outbox.pending} / ${view.outbox.inFlight} / ${view.outbox.failed}`,
            )}
            {row("Oldest pending", age(view.outbox.oldestPendingAgeMs))}
            {row("Published (last hour)", view.outbox.publishedLastHour)}
            {row(
              "Inbox received (24 h) / unfinished",
              `${view.inbox.received24h} / ${view.inbox.unfinished}`,
            )}
            {row(
              "Mail sent / bounced / complained / failed (24 h)",
              `${view.mail.sent24h} / ${view.mail.bounced24h} / ${view.mail.complained24h} / ${view.mail.failed24h}`,
            )}
            {row(
              "Attachments scanning",
              `${view.attachments.scanning} (oldest ${age(view.attachments.oldestScanningAgeMs)})`,
            )}
            {row("Infected (24 h)", view.attachments.infected24h)}
            {row("Drift events (24 h)", g["reconcile.drift_events_24h"] ?? 0)}
            {row("Integrity failures", g["reconcile.integrity_failures"] ?? 0)}
          </tbody>
        </table>
      </div>

      <h3>Failed outbox events</h3>
      {view.outbox.failedEvents.length === 0 ? <p>None.</p> : null}
      <ul>
        {view.outbox.failedEvents.map((e) => (
          <li key={e.id}>
            <code>{e.eventType}</code> for {e.aggregateType} {e.aggregateId} after {e.attempts}{" "}
            attempts: {e.lastError ?? "no error recorded"}{" "}
            {isAdmin ? (
              <>
                <button type="button" disabled={retrying} onClick={() => void retry(e.id, "same")}>
                  Retry
                </button>{" "}
                <button
                  type="button"
                  disabled={retrying}
                  onClick={() => void retry(e.id, "successor")}
                >
                  Retry as a new event
                </button>
              </>
            ) : null}
          </li>
        ))}
      </ul>

      <h3>Triage jobs awaiting recovery</h3>
      <p>
        These jobs have no recorded result after{" "}
        {Math.round((view.outbox.triageReplayAfterMs ?? 30 * 60_000) / 60_000)} minutes. Check the
        worker and dead-letter queues before retrying. Later triage jobs for the same request wait
        for recovery.
      </p>
      {(view.outbox.waitingTriageJobs ?? []).length === 0 ? <p>None.</p> : null}
      <ul>
        {(view.outbox.waitingTriageJobs ?? []).map((job) => (
          <li key={job.id}>
            Request {job.aggregateId}: waiting {age(job.waitingAgeMs)}.{" "}
            {isAdmin ? (
              <button type="button" disabled={retrying} onClick={() => void retry(job.id, "same")}>
                Retry job
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      <h3>Alarms and drift (24 h)</h3>
      {view.alarms.length === 0 ? <p>None.</p> : null}
      <ul>
        {view.alarms.map((a) => (
          <li key={a.id}>
            {new Date(a.createdAt).toLocaleString()} <code>{a.action}</code> {a.entityType}{" "}
            {a.entityId}
          </li>
        ))}
      </ul>
      <p role="status" aria-live="polite">
        {notice}
      </p>
    </section>
  );
}
